import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';
import { fetchIssues } from '../../services/issueService';
import type { IssueListItem } from '../../services/issueService';

/**
 * @deprecated CHG-052: 동적 계층 규칙에서는 서버의 allowedChildTypes 사용.
 * 폴백용으로 유지하되, 정규화된 이름도 포함.
 */
const ALLOWED_PARENT_TYPES: Record<string, string[] | null> = {
  EPIC: null,
  Epic: null,
  STORY: ['EPIC', 'Epic'],
  Story: ['EPIC', 'Epic'],
  TASK: ['STORY', 'Story'],
  Task: ['STORY', 'Story'],
  SUB_TASK: ['TASK', 'Task'],
  Subtask: ['TASK', 'Task'],
  BUG: ['EPIC', 'Epic', 'STORY', 'Story', 'TASK', 'Task', 'SUB_TASK', 'Subtask'],
  Bug: ['EPIC', 'Epic', 'STORY', 'Story', 'TASK', 'Task', 'SUB_TASK', 'Subtask'],
};

interface Props {
  projectId: string;
  issueType: string;
  currentIssueId?: string;
  value: string | null | undefined;
  onChange: (parentIssueId: string | null) => void;
}

export default function ParentIssueSelect({
  projectId,
  issueType,
  currentIssueId,
  value,
  onChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState<IssueListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const allowedTypes = ALLOWED_PARENT_TYPES[issueType];
  const isDisabled = allowedTypes === null; // EPIC cannot have parent

  // Load candidate parent issues
  const loadCandidates = useCallback(async () => {
    if (!allowedTypes || allowedTypes.length === 0) return;
    setLoading(true);
    try {
      const typeFilter = allowedTypes.map((t) => `issueType = "${t}"`).join(' OR ');
      const filter = search
        ? `(${typeFilter}) AND (issueId ~ "${search}" OR title ~ "${search}")`
        : typeFilter;
      const data = await fetchIssues(projectId, { filter, size: 50 });
      // Exclude self
      const filtered = currentIssueId
        ? data.items.filter((i) => i.id !== currentIssueId)
        : data.items;
      setCandidates(filtered);
    } catch {
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, allowedTypes, search, currentIssueId]);

  useEffect(() => {
    if (open) loadCandidates();
  }, [open, loadCandidates]);

  // Click outside to close
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Find selected issue label
  const selectedIssue = candidates.find((i) => i.issueId === value);
  const displayLabel = value
    ? selectedIssue
      ? `${selectedIssue.issueId} - ${selectedIssue.title}`
      : value
    : '';

  if (isDisabled) {
    return (
      <div className="px-3 py-2 text-sm text-gray-400 bg-gray-50 border border-gray-200 rounded-lg">
        에픽은 상위 작업을 가질 수 없습니다
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-left"
      >
        <span className={value ? 'text-gray-900 truncate' : 'text-gray-400'}>
          {displayLabel || '상위 작업 선택 (선택사항)'}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {value && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
              className="p-0.5 hover:bg-gray-200 rounded"
            >
              <X size={12} className="text-gray-400" />
            </span>
          )}
          <ChevronDown size={14} className="text-gray-400" />
        </div>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 flex flex-col">
          {/* Search */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="작업 검색..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-300"
                autoFocus
              />
            </div>
          </div>

          {/* "None" option */}
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
            className="px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 text-left border-b border-gray-100"
          >
            없음 (독립 작업)
          </button>

          {/* Candidates */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="text-center py-4 text-sm text-gray-400">로딩 중...</div>
            ) : candidates.length === 0 ? (
              <div className="text-center py-4 text-sm text-gray-400">
                선택 가능한 상위 작업이 없습니다
              </div>
            ) : (
              candidates.map((issue) => (
                <button
                  key={issue.id}
                  type="button"
                  onClick={() => {
                    onChange(issue.issueId);
                    setOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-sm hover:bg-gray-50 text-left flex items-center gap-2 ${
                    value === issue.issueId ? 'bg-blue-50' : ''
                  }`}
                >
                  <span className="text-sm text-gray-400 font-mono flex-shrink-0">
                    {issue.issueId}
                  </span>
                  <span className="truncate text-gray-700">{issue.title}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
